import { Box, Button, Typography } from "@mui/material";
import MultiSelect from "../../controls/MultiSelect";
import { AuditLogFilterType} from "../../../interface";
import styles from "./AuditLogFilter.module.scss";
import { DateRangeOptions} from "../../../utils/constants";
import { DateRangePicker } from "rsuite";
import dayjs from "dayjs";
import { getRangeDate } from "../../../utils/helper";
import { DateRange } from "rsuite/esm/DateRangePicker";

interface AuditLogFilterProps {
  filter: AuditLogFilterType;
  onChange: React.Dispatch<React.SetStateAction<AuditLogFilterType>>;
  onApply: () => void;
}

/**
 * AuditLogFilter component displays a set of dropdown filters
 * for narrowing down data on the audit log dashboard .
 *
 * It includes options to filter by:
 * - Date Range
 *
 * The component retrieves audit logs using `useAuditFilterLogDetails` and
 * in local component state.
 *
 * @returns {JSX.Element} The rendered AuditLogFilter component.
 */
const AuditLogFilter: React.FC<AuditLogFilterProps> = ({
  filter,
  onChange,
  onApply,
}) => {


  const selectConfigs = [
    {
      id: "dateRange",
      name: "dateRange",
      label: "Date range",
      value: filter.dateRange,
      options: DateRangeOptions,
      multiple: false,
      virtual: false,
      disabled: false,
    }
  ];

  const filterChangeHandler = (selection: any) => {
    if (selection.target.name === "dateRange") {
      const [start, end] = getRangeDate(selection.target.value);
      onChange((prev: any) => ({
        ...prev,
        dateRange: selection.target.value,
        startDate: start,
        endDate: end,
        monthSelected: selection.target.value === "thisMonth",
      }));
    }  else {
      onChange((prev: any) => ({
        ...prev,
        [selection.target.name]: selection.target.value,
      }));
    }
  };

  const handleCustomDateChange = (value: DateRange | null) => {
    if (!value) return;
    const [start, end] = [
      dayjs(value[0]).format("YYYY-MM-DD"),
      dayjs(value[1]).format("YYYY-MM-DD"),
    ];
    if (end < start) {
      onChange((prev: any) => ({
        ...prev,
        startDate: "",
        endDate: "",
      }));
      return;
    }
    onChange((prev: any) => ({
      ...prev,
      startDate: dayjs(start).format("YYYY-MM-DD"),
      endDate: dayjs(end).format("YYYY-MM-DD"),
    }));
  };

  return (
    <Box className={styles.filterWrapper}>
      <Typography className={styles.label}>Filter by:</Typography>
      <Box className={styles.selectContent}>
        {selectConfigs.map((config) => (
          <Box className={styles.selectContainer} key={config.id}>
            <MultiSelect 
              id={config.id}
              name={config.name}
              label={config.label}
              useMuiLabel
              showLabel
              value={config.value}
              options={config.options}
              onChange={filterChangeHandler}
              multiple={config.multiple}
              isVirtualized={config.virtual}
              disabled={config.disabled}
              testId={`test-${config.name}`}
            />
            {filter.dateRange === "custom" && config.name === "dateRange" && (
              <DateRangePicker
                size="lg"
                id="date-range"
                placeholder="Select Date Range"
                format="yyyy-MM-dd"
                menuStyle={{ zIndex: 9999 }}
                onChange={handleCustomDateChange}
                ranges={[]}
              />
            )}
          </Box>
        ))}
         <Button variant="contained" className={styles.applyButton} onClick={onApply}>
        Apply
      </Button>
      </Box>
     
    </Box>
  );
};

export default AuditLogFilter;
